import { ProjectRecord } from '@/lib/projects/project-types';
import { SourceRunStatus } from '@/lib/sources/source-types';
import { getSectorCounts as getSectorCountsRaw } from '@/lib/projects/project-utils';

export type ChartDatum = { name: string; value: number };

export function getSectorCounts(projects: ProjectRecord[]): ChartDatum[] {
  const raw = getSectorCountsRaw(projects);
  return Object.entries(raw)
    .sort((a, b) => b[1] - a[1])
    .map(([name, value]) => ({ name, value }));
}

export function getCountryCountsTop(
  projects: ProjectRecord[],
  topN = 8
): ChartDatum[] {
  const map = new Map<string, number>();
  for (const p of projects) {
    for (const c of p.country) {
      map.set(c, (map.get(c) || 0) + 1);
    }
  }
  const sorted = [...map.entries()].sort((a, b) => b[1] - a[1]);
  const top = sorted.slice(0, topN);
  const rest = sorted.slice(topN);
  const result = top.map(([name, value]) => ({ name, value }));
  if (rest.length > 0) {
    const otherTotal = rest.reduce((sum, [, v]) => sum + v, 0);
    result.push({ name: 'Other', value: otherTotal });
  }
  return result;
}

export function getProjectsOverTime(
  projects: ProjectRecord[],
  mode: 'weekly' | 'daily' = 'weekly'
): ChartDatum[] {
  const now = new Date();
  const buckets = new Map<string, number>();

  if (mode === 'weekly') {
    // Last 12 weeks
    for (let i = 11; i >= 0; i--) {
      const weekStart = new Date(now);
      weekStart.setDate(weekStart.getDate() - i * 7);
      const label = `W${weekStart.toISOString().slice(5, 10)}`;
      buckets.set(label, 0);
    }
    for (const p of projects) {
      const pDate = new Date(p.updatedAt);
      const diffMs = now.getTime() - pDate.getTime();
      const diffWeeks = Math.floor(diffMs / (7 * 24 * 60 * 60 * 1000));
      if (diffWeeks >= 0 && diffWeeks < 12) {
        const weekStart = new Date(now);
        weekStart.setDate(weekStart.getDate() - diffWeeks * 7);
        const label = `W${weekStart.toISOString().slice(5, 10)}`;
        buckets.set(label, (buckets.get(label) || 0) + 1);
      }
    }
  } else {
    // Last 30 days
    for (let i = 29; i >= 0; i--) {
      const day = new Date(now);
      day.setDate(day.getDate() - i);
      const label = day.toISOString().slice(5, 10);
      buckets.set(label, 0);
    }
    for (const p of projects) {
      const label = new Date(p.updatedAt).toISOString().slice(5, 10);
      if (buckets.has(label)) {
        buckets.set(label, (buckets.get(label) || 0) + 1);
      }
    }
  }

  return [...buckets.entries()].map(([name, value]) => ({ name, value }));
}

export function getCfpRate(projects: ProjectRecord[]): {
  withCfp: number;
  withoutCfp: number;
  percent: number;
} {
  const withCfp = projects.filter((p) => p.callForProposalLink !== null).length;
  const withoutCfp = projects.length - withCfp;
  const percent = projects.length > 0 ? Math.round((withCfp / projects.length) * 100) : 0;
  return { withCfp, withoutCfp, percent };
}

export function getTopOrganizations(
  projects: ProjectRecord[],
  topN = 5
): ChartDatum[] {
  const map = new Map<string, number>();
  for (const p of projects) {
    map.set(p.organizationName, (map.get(p.organizationName) || 0) + 1);
  }
  return [...map.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, topN)
    .map(([name, value]) => ({ name, value }));
}

export type SourceHealthData = {
  ok: number;
  warning: number;
  error: number;
  total: number;
  pieData: ChartDatum[];
  failedList: SourceRunStatus[];
  warningList: SourceRunStatus[];
};

export function getSourceHealth(
  sourceStatuses: SourceRunStatus[]
): SourceHealthData {
  let ok = 0;
  let warning = 0;
  let error = 0;
  const failedList: SourceRunStatus[] = [];
  const warningList: SourceRunStatus[] = [];

  for (const s of sourceStatuses) {
    if (s.status === 'ok') ok++;
    else if (s.status === 'warning') {
      warning++;
      warningList.push(s);
    } else if (s.status === 'error') {
      error++;
      failedList.push(s);
    }
  }

  const pieData: ChartDatum[] = [];
  if (ok > 0) pieData.push({ name: 'OK', value: ok });
  if (warning > 0) pieData.push({ name: 'Warning', value: warning });
  if (error > 0) pieData.push({ name: 'Failed', value: error });

  return {
    ok,
    warning,
    error,
    total: sourceStatuses.length,
    pieData,
    failedList,
    warningList,
  };
}
